import { Component } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { last, first, get, findIndex } from "lodash"
import { safeDivide } from "helpers/math"
import Axis from "../../axis"
import Dot from "../../dot"
import { MARGINS } from "../../helper"
import Tooltip from "../tooltip"
import "../../style.scss"

class LineTargetChart extends Component {
  static propTypes = {
    data: PropTypes.array.isRequired,
    dots: PropTypes.array,
    resizableRef: PropTypes.func.isRequired,
    line: PropTypes.object,
    xAxis: PropTypes.object,
    yAxis: PropTypes.object,
    highlighter: PropTypes.object,
    height: PropTypes.number,
    width: PropTypes.number.isRequired,
    bestPoint: PropTypes.object,
    originalPoint: PropTypes.object,
    scaleY: PropTypes.func.isRequired,
    scaleX: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
    unsubscribe: PropTypes.func.isRequired,
    setSelectedData: PropTypes.func,
    TooltipContent: PropTypes.any.isRequired,
    disabledClick: PropTypes.bool,
    tooltipTitle: PropTypes.string,
    focused: PropTypes.shape({
      color: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
  }

  state = {
    focusedPoint: null,
  }

  findByOffset = offset => {
    const { width, data, scaleX } = this.props
    const lineWidth = width - (MARGINS.left + 40) - (MARGINS.right)
    const cursorX = offset - (MARGINS.left + 20)
    const pointIncrement = (last(data).x - first(data).x) / 200
    const totalNumberOfPoints = lineWidth / 200
    const totalNumberOfPointsFromCursor = safeDivide(cursorX, totalNumberOfPoints)
    const firstDataPoint = { x: get(data, [0, "x"], 0), y: get(data, [0, "y"], 0) }

    if (totalNumberOfPointsFromCursor >= 0) {
      if (totalNumberOfPointsFromCursor >= 200) {
        return { x: get(data, [data.length - 1, "x"], 0), y: get(data, [data.length - 1, "y"], 0) }
      } else if (totalNumberOfPointsFromCursor <= 0) {
        return firstDataPoint
      }

      const lookup = scaleX.invert(offset - 2) // -2 is to give a margin of selection on the first element
      const pointIndex = findIndex(data, datum => datum.x >= lookup)

      if (pointIndex < 1) {
        return firstDataPoint
      } else {
        const pointBeforeCursor = { x: get(data, [pointIndex - 1, "x"], 0), y: get(data, [pointIndex - 1, "y"], 0) }
        const pointAfterCursor = { x: get(data, [pointIndex, "x"], 0), y: get(data, [pointIndex, "y"], 0) }
        const numberOfPointsFromBeforeCursor = safeDivide((scaleX(pointBeforeCursor.x) - (MARGINS.left + 20)), totalNumberOfPoints)
        const numberOfPointsFromAfterCursor = safeDivide((scaleX(pointAfterCursor.x) - (MARGINS.left + 20)), totalNumberOfPoints)

        const numberOfPointsInCursorRange = numberOfPointsFromAfterCursor - numberOfPointsFromBeforeCursor
        const numberOfPointsToCursor = totalNumberOfPointsFromCursor - numberOfPointsFromBeforeCursor
        const pointValueInRange = safeDivide((pointAfterCursor.y - pointBeforeCursor.y), numberOfPointsInCursorRange)

        return {
          x: firstDataPoint.x + (pointIncrement * (totalNumberOfPointsFromCursor - 1)),
          y: pointBeforeCursor.y + (pointValueInRange * (numberOfPointsToCursor - 1)),
        }
      }
    }

    return null
  }

  setSelectedData = offset => {
    const { setSelectedData } = this.props
    const point = this.findByOffset(offset)
    if (point) {
      setSelectedData(point)
    }
  }

  handleGraphClick = ev => {
    const { disabledClick } = this.props

    if (!disabledClick) {
      const box = ev.currentTarget.getBoundingClientRect()
      const offsetX = ev.pageX - box.left
      this.setSelectedData(offsetX)
    }
  }

  onPointFocus = focusedPoint => {
    this.setState({ focusedPoint })
  }

  onPointClick = (point) => {
    const { setSelectedData, disabledClick } = this.props

    if (!disabledClick) {
      setSelectedData(point)
    }
  }

  render() {
    const {
      data,
      resizableRef,
      line,
      xAxis,
      yAxis,
      highlighter,
      scaleY,
      scaleX,
      subscribe,
      unsubscribe,
      height,
      width,
      dots,
      tooltipTitle,
      TooltipContent,
      disabledClick,
      focused,
    } = this.props
    const { focusedPoint } = this.state

    if (!data.length) {
      return <div ref={resizableRef} className="components_charts_line-target no-data">No Data</div>
    } else {
      return <div className="components_charts_line-target">
        <svg ref={resizableRef} className={classNames({ disabled: disabledClick })} style={{ height }}>
          <Axis {...xAxis} className="x-axis" />
          <Axis {...yAxis} />
          {yAxis.label.text
            && <text transform={yAxis.label.transform} dy={yAxis.label.dy} x={yAxis.label.x} y={yAxis.label.y} className="baseline-label">
              {yAxis.label.text}
            </text>}
          {xAxis.label.text
            && <text transform={xAxis.label.transform} className="baseline-label x-axis-label">
              {xAxis.label.text}
            </text>}
          <line {...xAxis.baseline} className="baseline" />
          <line {...yAxis.baseline} className="baseline" />
          <path className={classNames("path")} {...line} />
          {highlighter
            && <g>
              <text className="highlighter-label" {...highlighter.label}>{highlighter.label.text}</text>
              <line className="highlighter" {...highlighter.line} />
            </g>}
          <rect onClick={this.handleGraphClick} className="clickable-rect" height={height} width={width} />
          {dots.map((dot, i) =>
            <Dot
              focused={focused}
              key={i}
              dot={dot}
              onClick={this.onPointClick}
              onFocus={this.onPointFocus}
              y={scaleY(dot.y)}
              x={scaleX(dot.x)}
            />)}
        </svg>
        <Tooltip
          {...{
            subscribe,
            findByOffset: this.findByOffset,
            unsubscribe,
            focusedPoint,
            title: tooltipTitle,
            TooltipContent,
          }}
        />
      </div>
    }
  }
}

export default LineTargetChart
